import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, Bell, KeyRound, Lock, MonitorSmartphone, ShieldAlert, Users } from "lucide-react";
import { PageHeader, StatusBadge } from "@/components/shared/Common";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { fmtRelative } from "@/lib/format";

const Kpi = ({ icon: Icon, label, value, to, tone = "green", testid }) => {
  const body = (
    <div data-testid={testid} className="syc-card p-5 h-full hover:-translate-y-0.5 transition-transform">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-[0.12em] text-[#5b6656]">{label}</span>
        <Icon size={18} className={tone === "red" ? "text-[#b42318]" : tone === "yellow" ? "text-[#c9a400]" : "text-[#258F00]"} />
      </div>
      <div className="mt-3 text-3xl font-extrabold tabular-nums">{value ?? "—"}</div>
    </div>
  );
  return to ? <Link to={to}>{body}</Link> : body;
};

const Phases = ({ phases }) => (
  <div className="syc-card p-6" data-testid="phases-roadmap">
    <h2 className="text-base md:text-lg font-bold mb-4">Hoja de ruta SYC CONTROL</h2>
    <ol className="space-y-2.5">
      {phases.map((p) => (
        <li key={p.n} className="flex items-start gap-3">
          <span className={`mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full text-[11px] font-bold ${p.status === "activo" ? "bg-[#258F00] text-white" : "bg-[#F7F7F7] text-[#8a9584] border border-[#e4e8e1]"}`}>{p.n}</span>
          <div className="min-w-0">
            <div className="text-sm font-semibold flex items-center gap-2">{p.name}{p.status === "activo" && <StatusBadge status="activo" label="En uso" />}</div>
            <div className="text-xs text-[#5b6656]">{p.desc}</div>
          </div>
        </li>
      ))}
    </ol>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)); }, []);
  if (!d) return <div className="text-[#5b6656]" data-testid="dashboard-loading">Cargando…</div>;

  return (
    <div data-testid="dashboard-page">
      <PageHeader eyebrow="Dashboard base" title={`Hola, ${user.name.split(" ")[0]}`}
        subtitle="Indicadores calculados en tiempo real desde los registros del sistema. Los módulos de negocio se habilitarán por fases." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {d.users && <Kpi testid="kpi-users-active" icon={Users} label="Usuarios activos" value={d.users.activos} to="/admin/usuarios" />}
        {d.users && <Kpi testid="kpi-users-locked" icon={Lock} label="Bloqueados" value={d.users.bloqueados} tone={d.users.bloqueados ? "red" : "green"} to="/admin/usuarios" />}
        {d.sessions_active !== undefined && <Kpi testid="kpi-sessions" icon={MonitorSmartphone} label="Sesiones activas" value={d.sessions_active} to="/admin/sesiones" />}
        {d.roles !== undefined && <Kpi testid="kpi-roles" icon={KeyRound} label="Roles activos" value={d.roles} to="/admin/roles" />}
        {d.audit_24h !== undefined && <Kpi testid="kpi-audit-24h" icon={Activity} label="Eventos 24 h" value={d.audit_24h} to="/admin/auditoria" />}
        {d.failed_logins_24h !== undefined && <Kpi testid="kpi-failed-logins" icon={ShieldAlert} label="Logins fallidos 24 h" value={d.failed_logins_24h} tone={d.failed_logins_24h ? "yellow" : "green"} to="/admin/auditoria" />}
        <Kpi testid="kpi-notifications" icon={Bell} label="Notificaciones" value={d.unread_notifications} to="/notificaciones" />
      </div>
      <div className="grid lg:grid-cols-[1fr_380px] gap-6">
        {d.recent_audit ? (
          <div className="syc-card overflow-hidden" data-testid="recent-audit">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#e4e8e1]">
              <h2 className="text-base md:text-lg font-bold">Actividad reciente</h2>
              <Link to="/admin/auditoria" className="text-sm font-semibold text-[#258F00]" data-testid="recent-audit-see-all">Ver auditoría</Link>
            </div>
            <ul>
              {d.recent_audit.map((a) => (
                <li key={a.id} className="flex items-center gap-4 px-6 py-3 border-b border-[#f0f2ee] last:border-0 text-sm">
                  <span className="font-mono text-[11px] text-[#8a9584] w-10">#{a.seq}</span>
                  <span className="font-semibold font-mono text-[12px] text-[#1c5f00] truncate">{a.action}</span>
                  <span className="text-[#5b6656] truncate hidden sm:block">{a.user_name || "—"}</span>
                  <span className="ml-auto text-xs text-[#8a9584] whitespace-nowrap">{fmtRelative(a.ts)}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : <div className="syc-card p-6 text-sm text-[#5b6656]">Su perfil no tiene indicadores administrativos. Los dashboards de su área se habilitarán con el módulo correspondiente.</div>}
        <Phases phases={d.phases} />
      </div>
    </div>
  );
}
